import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import moment from 'moment';
import numeral from 'numeral';
import {connect} from 'react-redux';

import MillieApi from 'app/apis/millie';
import Icon      from 'app/components/common/icon';
import Modal     from 'app/components/common/modal';
import {
  NONPROFIT_DEFAULT_IMG_URL,
  EmployeeDonationMatchStatuses as MatchStatuses,
  PercentPaymentMethods as Pms,
}                from 'app/constants';
import format    from 'app/helpers/format';
import AuthSlx   from 'app/selectors/auth';

const Statuses = {
  PENDING:          {val:  0, color: 'gray',  label: 'Pending',          name: ''},
  NOT_MATCHED:      {val:  1, color: 'gray',  label: 'Not Matched',      name: ''},
  DONATION_MADE:    {val: 10, color: 'green', label: 'Donation Made',    name: 'Donation Made'},
  PAYMENT_RECEIVED: {val: 20, color: 'green', label: 'Payment Received', name: 'Payment Received'},
  DISB_INITIATED:   {val: 30, color: 'green', label: 'Initiated',        name: 'Disbursement Initiated'},
  DISBURSED:        {val: 40, color: 'green', label: 'Disbursed',        name: 'Disbursed to Organization'},
};

const dafName = 'Percent Impact Foundation (dba Goodstack)';

class ModalDonationTracker extends React.PureComponent {

  constructor(props) {
    super(props);

    this.state = {
      loading: true,
      data: null,
      tabIndex: null,
    }

    this.onClose = this.onClose.bind(this);
  }

  componentDidMount() {
    this.setState({loading: true});
    const params = _.pick(this.props, ['donationId', 'employeeDonationId', 'grantId']);
    MillieApi.donationTrack(params).then((data) => {
      this.setState({data, loading: false});
      // this.setState({data, loading: true});
    });
  }

  get objects() {
    const {data} = this.state;
    const {hideNoMatch} = this.props;
    if (!data) return [];
    const {employeeDonation: ed, donation, grant} = data;
    const objs = [];
    if (donation)    objs.push({...donation, type: 'Donation'});
    if (grant)       objs.push({...grant, type: 'Grant'});
    if (ed) {
      const hideStatuses = [MatchStatuses.INELIGIBLE, MatchStatuses.NO_MATCH, MatchStatuses.DECLINED];
      const showMatch = !hideStatuses.includes(ed.matchStatus);
      if (showMatch) objs.push({...ed, type: 'Match'});
    }
    return objs;
  }

  get activeObject() {
    return this.objects[this.tabIndex];
  }

  get isNotPercent() {
    const daf = this.activeObject?.disbursement?.daf;
    return !!(daf && daf !== 'percent');
  }

  get status() {
    const object = this.activeObject;
    if (!object) return null;
    const {disbursement: disb} = object;

    if (!disb) {
      if (object.type !== 'Match') return Statuses.DONATION_MADE;
      if (object.matchStatus === MatchStatuses.PENDING) return Statuses.PENDING;
      if (object.matchStatus === MatchStatuses.APPROVED) return Statuses.DONATION_MADE;
      return Statuses.NOT_MATCHED;
    }
    if (!disb.disbursedAt) return Statuses.PAYMENT_RECEIVED;
    if (disb.percentStatus === 'DISBURSED') return Statuses.DISBURSED;
    return Statuses.DISB_INITIATED;
  }

  get tabIndex() {
    if (_.isFinite(this.state.tabIndex)) return this.state.tabIndex;
    if (this.props.employeeDonationId) {
      const index = this.objects.findIndex(o => o.id === this.props.employeeDonationId);
      if (index >= 0) return index;
    }
    return 0;
  }

  get showFeeBlurb() {
    const company = this.props.currentUser?.employment?.company;
    if (!company) return false;
    return !!(company.feesCoverDonation || company.feesCoverMatch);
  }

  getSteps() {
    if (this.isNotPercent) return [{title: 'Disbursed', isComplete: true, desc: 'This donation was disbursed via Givinga Foundation quite some time ago.'}];
    const object = this.activeObject;
    if (object.type === 'Donation') return this.getDonationSteps(object);
    if (object.type === 'Match')    return this.getMatchSteps(object);
    if (object.type === 'Grant')    return this.getGrantSteps(object);
    return [];
  }

  getDisbSteps(disb) {
    const steps = [];
    const pmBits = (() => {
      if (disb?.percentPaymentMethod === 'cheque') return [' via check', 'Check Cashed'];
      if (disb?.percentPaymentMethod === 'bank_transfer') return [' via electronic transfer', 'Electronic Transfer Received'];
      if (disb?.percentPaymentMethod === 'card') return [' via credit card', 'Payment Received'];
      return ['', 'Payment Received'];
    })();
    // disbursed millie->percent
    steps.push({
      title: 'Disbursement Initiated',
      dateStr: (disb?.disbursedDateStr || null),
      desc: 'Instructions have been sent to disbursement partner, and they have it in the queue.',
    });
    // sent percent->np
    steps.push({
      title: 'Payment Sent to Nonprofit',
      dateStr: (disb?.percentPaymentInitiatedDateStr || disb?.percentPaidDateStr || null),
      desc: `Payment is on its way${pmBits[0]} from ${dafName} to the nonprofit.`,
    });
    // done
    steps.push({
      title: pmBits[1],
      dateStr: (disb?.percentPaidDateStr || null),
      desc: 'Nonprofit has received the funds.',
      amount: disb?.actualAmountInCents,
    });
    return steps;
  }
  getDonationSteps(donation) {
    return [
      {
        title: 'Donation Made',
        dateStr: donation.createdDateStr || null,
        desc: `Payment has been received by disbursement partner ${dafName}.`,
        amount: donation.amountInCents,
      },
      ...this.getDisbSteps(donation.disbursement),
    ];
  }
  getMatchSteps(match) {
    return [
      {
        title: 'Match Approved',
        dateStr: match.processedDateStr || null,
        desc: 'Employer has approved the match amount.',
        amount: match.matchedAmountInCents || null,
      },      
      {
        title: 'Donation Made',
        dateStr: match.billedDateStr || null,
        desc: 'Payment has been received by disbursement partner Percent Impact Foundation (dba GoodStack).',
      },
      ...this.getDisbSteps(match.disbursement),
    ];
  }
  getGrantSteps(grant) {
    return [
      {
        title: 'Grant Made',
        dateStr: grant.createdDateStr || null,
        desc: `Payment has been received by disbursement partner ${dafName}.`,
        amount: grant.amountInCents,
      },
      ...this.getDisbSteps(grant.disbursement),
    ];
  }

  onClose() {
    this.props.onClose();
  }

  onClickTab(tabIndex) {
    this.setState({tabIndex});
  }

  renderTabs() {
    const {objects, tabIndex} = this;
    const {hide1Tab} = this.props;
    const showTabs = !hide1Tab || (objects.length > 1);

    return (
      <div className={`dontrack-head-tabs ${showTabs ? 'show' : 'hide'}`}>
        {objects.map((obj, i) => {
          const isActive = i === tabIndex;
          return (
            <div key={obj.id} className={`dontrack-head-tabs-tab ${isActive ? 'active' : ''}`} onClick={this.onClickTab.bind(this, i)}>{obj.type}</div>
          );
        })}
      </div>
    );
  }

  renderObject() {
    const {loading} = this.state;
    if (loading) {
      return (
        <div className="dontrack-loading">
          <Icon.Loading />
        </div>
      );
    }

    const object = this.activeObject;
    if (!object) return null;
    const {disbursement: disb, nonprofit} = object;
    const status = this.status;
    const isDon = object.type === 'Donation';
    const showDetails = !!disb?.note || isDon;

    return (
      <div className="dontrack-obj">
        {disb && (
          <div className="dontrack-obj-id">Tracking ID: {disb.id}</div>
        )}
        {/* <div className="dontrack-obj-status"> */}
        {/*   Live Status: */}
        {/*   <span className={`dontrack-obj-status-tag ${status.color}`}>{status.label}</span> */}
        {/* </div> */}
        {this.renderSteps()}
        {nonprofit && (
          <div className="dontrack-obj-org">
            <img src={nonprofit.imgUrl || NONPROFIT_DEFAULT_IMG_URL} className="dontrack-obj-org-img" />
            <div className="dontrack-obj-org-text">
              <div className="dontrack-obj-org-text-name">{nonprofit.name}</div>
              <div className="dontrack-obj-org-text-num">
                {nonprofit.ein ? 'EIN: ' : (nonprofit.registry?.name ? `${nonprofit.registry.name} Number: ` : '')}
                {nonprofit.ein ? nonprofit.ein : nonprofit.registeredNumber}
              </div>
            </div>
          </div>
        )}
        {showDetails && (
          <div className="dontrack-obj-details">
            {isDon && (
              <div className={`dontrack-obj-details-anon ${object.anonymous ? 'anon-yes' : 'anon-no'}`}>
                {object.anonymous ? 'Anonymous donation' : `Donor name & email shared with organization.`}
              </div>
            )}
            {disb?.note && (
              <div className="dontrack-obj-details-note">
                NOTE:
                <br />
                {disb.note}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  renderSteps() {
    const steps = this.getSteps();

    return (
      <div className="dontrack-steps">
        {steps.map((step, i) => {
          const nextStep = steps[i + 1];
          const isFinal = !nextStep;
          const isNextStepComplete = !!nextStep?.dateStr;
          const isComplete = !!(step.dateStr || step.isComplete);
          const dateFmt = step.dateStr ? moment.utc(step.dateStr).format('dddd, MMMM Do, YYYY') : '-';
          const amountFmt = _.isFinite(step.amount) ? format.usd(step.amount) : '';
          return (
            <div key={i} className={`dontrack-step ${isComplete ? 'complete' : ''}`}>
              <div className="dontrack-step-status">
                <Icon.Check1 />
              </div>
              {!isFinal && <div className={`dontrack-step-line ${isNextStepComplete ? 'filled' : ''}`}></div>}
              <div className="dontrack-step-text">
                <div className="dontrack-step-text-name">{step.title}</div>
                <div className="dontrack-step-text-date">{dateFmt}</div>
                <div className="dontrack-step-text-desc">{step.desc}</div>
                {amountFmt && <div className="dontrack-step-text-amount">{amountFmt}</div>}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  render() {
    return (
      <Modal onClose={this.onClose} className="dontrack">
        <div className="dontrack-head">
          <h1 className="dontrack-head-h1 rainbow-text"><Icon.ShipmentBox /> Donation Tracker</h1>
          {this.renderTabs()}
        </div>
        {this.renderObject()}
        {this.showFeeBlurb && (
          <p className="notice _mx-48 _mt-0 _mb-48">This tracker shows the donation or match amount only. Covered fees are not reflected in the amounts shown.</p>
        )}
      </Modal>
    );
  }

}

ModalDonationTracker.propTypes = {
  onClose: PropTypes.func.isRequired,
  donationId: PropTypes.string,
  employeeDonationId: PropTypes.string,
  grantId: PropTypes.string,
  hideNoMatch: PropTypes.bool,
  hide1Tab: PropTypes.bool,
};

ModalDonationTracker.defaultProps = {
  hideNoMatch: false,
  hide1Tab: false,
};

const stateToProps = (state) => ({
  currentUser: AuthSlx.currentUser(state),
});
const dispatchToProps = (dispatch) => ({});

export default connect(stateToProps, dispatchToProps)(ModalDonationTracker);
